import React from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { Region, State, AnalyticsFilters } from '../../types/analytics';

interface RegionStateFilterProps {
  filters: AnalyticsFilters;
  regions: Region[];
  states: State[];
  onFilterChange: <K extends keyof AnalyticsFilters>(key: K, value: AnalyticsFilters[K]) => void;
  onReset: () => void;
  disabled?: boolean;
}

export const RegionStateFilter: React.FC<RegionStateFilterProps> = ({
  filters,
  regions,
  states,
  onFilterChange,
  onReset,
  disabled = false
}) => {
  const hasActiveFilters = filters.regionId || filters.stateId;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Region Dropdown */}
      <div className="relative">
        <select
          value={filters.regionId || ''}
          onChange={(e) => onFilterChange('regionId', e.target.value ? Number(e.target.value) : undefined)}
          disabled={disabled}
          className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
        >
          <option value="">All Regions</option>
          {regions.map((region) => (
            <option key={region.roId} value={region.roId}>
              {region.roName}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
      </div>

      {/* State Dropdown */}
      <div className="relative">
        <select
          value={filters.stateId || ''}
          onChange={(e) => onFilterChange('stateId', e.target.value ? Number(e.target.value) : undefined)}
          disabled={disabled}
          className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
        >
          <option value="">All States</option>
          {states.map((state) => (
            <option key={state.stateId} value={state.stateId}>
              {state.stateName}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <button
          onClick={onReset}
          disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />
          Clear
        </button>
      )}

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 ml-2">
          {filters.regionId && (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
              {regions.find(r => r.roId === filters.regionId)?.roName}
              <button
                onClick={() => onFilterChange('regionId', undefined)}
                className="hover:bg-blue-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.stateId && (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
              {states.find(s => s.stateId === filters.stateId)?.stateName}
              <button
                onClick={() => onFilterChange('stateId', undefined)}
                className="hover:bg-purple-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default RegionStateFilter;
